import { type ReactNode } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

interface QueryProviderProps {
  children: ReactNode
}

// Create a query client with sensible defaults for our mobile-first app
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache data for 5 minutes by default
      staleTime: 5 * 60 * 1000,
      // Keep inactive queries in cache for 10 minutes
      gcTime: 10 * 60 * 1000,
      // Custom retry logic that doesn't retry on auth errors
      retry: (failureCount, error) => {
        // Don't retry on authentication (401) or authorization (403) errors
        if (
          error &&
          typeof error === 'object' &&
          'status' in error &&
          ((error as any).status === 401 || (error as any).status === 403)
        ) {
          return false;
        }
        // Retry up to 3 times for other errors
        return failureCount < 3;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      // Refetch on window focus for fresh data
      refetchOnWindowFocus: true,
      // Don't refetch on reconnect by default (mobile battery optimization)
      refetchOnReconnect: false,
    },
    mutations: {
      // Custom retry logic for mutations
      retry: (failureCount, error) => {
        // Don't retry on authentication (401) or authorization (403) errors
        if (
          error &&
          typeof error === 'object' &&
          'status' in error &&
          ((error as any).status === 401 || (error as any).status === 403)
        ) {
          return false;
        }
        // Retry once for other errors
        return failureCount < 1;
      },
    },
  },
})

export function QueryProvider({ children }: QueryProviderProps) {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
    </QueryClientProvider>
  )
}